import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { queryEvents, queryStorageLocations, Trip } from '../schema';
import { db } from '../instant';
import { usePackingListsByEvent } from '../hooks/usePackingListsByEvent';
import { getPackingListStats } from '../utils/packingListStats';
import { inferRouteStructure } from '../utils/routeInference';
import { buildTripDagFromRouteStructure } from '../utils/tripDag';
import { rebuildTripDag } from '../utils/tripDagDb';
import { rebuildTripLpSolution, buildRequirementsFromPackingLists } from '../utils/tripLpDb';
import { TRIP_PLAN_LOCK_MESSAGE, useTripLock } from '../hooks/useTripLock';

interface Props {
    open: boolean;
    onClose: () => void;
    trip: Trip | null;
    onSaved?: () => void;
}

export default function EditTripDialog({ open, onClose, trip, onSaved }: Props) {
    const { lockedTripIds } = useTripLock();
    const tripLocked = trip ? lockedTripIds.has(trip.id) : false;
    const { data: eventsData } = queryEvents();
    const events = useMemo(() => eventsData?.events || [], [eventsData]);
    const { data: storageLocationsData } = queryStorageLocations();
    const storageLocations = useMemo(
        () => [...(storageLocationsData?.storageLocations ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [storageLocationsData]
    );

    const { packingListItemsByEvent, eventsWithPackingLists } = usePackingListsByEvent(events);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sourceStorageLocationId, setSourceStorageLocationId] = useState<string | null>(null);
    const [destStorageLocationId, setDestStorageLocationId] = useState<string | null>(null);
    const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Sync form when trip or open state changes
    useEffect(() => {
        if (open && trip) {
            setName(trip.name ?? '');
            setDescription(trip.description ?? '');
            const src = trip.sourceStorageLocation;
            const dest = trip.destStorageLocation;
            setSourceStorageLocationId(src?.id ?? null);
            setDestStorageLocationId(dest?.id ?? null);
            const eventIds = (trip.events ?? []).map(e => e.id);
            const idsInOptions = new Set(eventsWithPackingLists.map(e => e.id));
            setSelectedEventIds(new Set(eventIds.filter(id => idsInOptions.has(id))));
            setValidationError(null);
        }
    }, [open, trip, eventsWithPackingLists]);

    const handleSubmit = async () => {
        if (!trip) return;
        if (tripLocked) {
            setValidationError('Trip has an operation in progress; finish or complete it to edit.');
            return;
        }
        setValidationError(null);
        const trimmedName = name.trim();
        if (!trimmedName) {
            setValidationError('Name is required');
            return;
        }
        if (!sourceStorageLocationId) {
            setValidationError('Source storage location (before events) is required');
            return;
        }
        if (!destStorageLocationId) {
            setValidationError('Destination storage location (after events) is required');
            return;
        }

        setIsSaving(true);
        try {
            const eventIds = Array.from(selectedEventIds);
            const removedEventIds = (trip.events ?? []).map(e => e.id).filter(id => !eventIds.includes(id));
            const prevSource = trip.sourceStorageLocation?.id;
            const prevDest = trip.destStorageLocation?.id;
            const txs: Parameters<typeof db.transact>[0] = [
                db.tx.trips[trip.id].update({
                    name: trimmedName,
                    description: description.trim() || undefined,
                    notes: undefined,
                }),
                ...(prevSource && prevSource !== sourceStorageLocationId
                    ? [db.tx.trips[trip.id].unlink({ sourceStorageLocation: prevSource })]
                    : []),
                ...(prevDest && prevDest !== destStorageLocationId ? [db.tx.trips[trip.id].unlink({ destStorageLocation: prevDest })] : []),
                db.tx.trips[trip.id].link({ sourceStorageLocation: sourceStorageLocationId }),
                db.tx.trips[trip.id].link({ destStorageLocation: destStorageLocationId }),
                ...(removedEventIds.length > 0 ? [db.tx.trips[trip.id].unlink({ events: removedEventIds })] : []),
                db.tx.trips[trip.id].link({ events: eventIds }),
            ];
            await db.transact(txs);

            const eventsMap = new Map(
                events
                    .filter(e => selectedEventIds.has(e.id))
                    .map(e => [
                        e.id,
                        {
                            id: e.id,
                            name: e.name,
                            startDate: e.startDate ? new Date(e.startDate as unknown as string) : null,
                            endDate: e.endDate ? new Date(e.endDate as unknown as string) : null,
                        },
                    ])
            );
            const packingListsForInference = new Map(
                eventIds.map(eid => {
                    const items = packingListItemsByEvent.get(eid) ?? [];
                    return [
                        eid,
                        items.map(item => ({
                            itemType: item.itemType
                                ? {
                                      id: item.itemType.id,
                                      category: item.itemType.category ? { name: item.itemType.category.name } : null,
                                  }
                                : null,
                        })),
                    ];
                })
            );
            const routeStructure = inferRouteStructure(
                [],
                eventIds,
                eventsMap,
                packingListsForInference,
                [sourceStorageLocationId],
                [destStorageLocationId]
            );
            const tripWithDag = trip as { tripDagNodes?: { id: string }[]; tripDagEdges?: { id: string }[] };
            await rebuildTripDag(
                trip.id,
                tripWithDag.tripDagNodes ?? [],
                tripWithDag.tripDagEdges ?? [],
                routeStructure,
                sourceStorageLocationId,
                destStorageLocationId
            );

            const dag = buildTripDagFromRouteStructure(routeStructure, sourceStorageLocationId, destStorageLocationId);
            if (dag.nodes.length > 0) {
                const packingListsByEvent = new Map<string, Array<{ itemTypeId: string; quantity: number }>>();
                const consumableByItemType: Record<string, boolean> = {};
                for (const eid of Array.from(selectedEventIds)) {
                    const items = packingListItemsByEvent.get(eid) ?? [];
                    const list: Array<{ itemTypeId: string; quantity: number }> = [];
                    for (const i of items) {
                        const it = i.itemType;
                        if (!it?.id) continue;
                        consumableByItemType[it.id] = (it as { consumable?: boolean }).consumable ?? false;
                        list.push({ itemTypeId: it.id, quantity: i.quantity ?? 0 });
                    }
                    if (list.length > 0) packingListsByEvent.set(eid, list);
                }
                const requirements = buildRequirementsFromPackingLists(
                    dag.nodes.map(n => ({ index: n.index, kind: n.kind, eventId: n.eventId ?? null })),
                    packingListsByEvent
                );
                await rebuildTripLpSolution(
                    trip.id,
                    dag.nodes.map(n => ({ index: n.index, kind: n.kind, eventId: n.eventId })),
                    dag.edges,
                    requirements,
                    consumableByItemType
                );
            }

            onClose();
            onSaved?.();
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to save trip';
            setValidationError(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setValidationError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Trip</DialogTitle>
            <DialogContent>
                {tripLocked && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {TRIP_PLAN_LOCK_MESSAGE}
                    </Alert>
                )}
                <TextField
                    autoFocus
                    margin="dense"
                    label="Trip name"
                    fullWidth
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                />
                <TextField
                    margin="dense"
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Source (before events)
                </Typography>
                <Autocomplete
                    options={storageLocations}
                    getOptionLabel={option => option.name ?? ''}
                    value={storageLocations.find(s => s.id === sourceStorageLocationId) ?? null}
                    onChange={(_e, v) => {
                        setSourceStorageLocationId(v?.id ?? null);
                        setValidationError(null);
                    }}
                    renderInput={params => <TextField {...params} placeholder="Select source storage location" size="small" required />}
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Destination (after events)
                </Typography>
                <Autocomplete
                    options={storageLocations}
                    getOptionLabel={option => option.name ?? ''}
                    value={storageLocations.find(s => s.id === destStorageLocationId) ?? null}
                    onChange={(_e, v) => {
                        setDestStorageLocationId(v?.id ?? null);
                        setValidationError(null);
                    }}
                    renderInput={params => (
                        <TextField {...params} placeholder="Select destination storage location" size="small" required />
                    )}
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Events in this trip
                </Typography>
                {eventsWithPackingLists.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No events with packing lists
                    </Typography>
                ) : (
                    <Autocomplete
                        multiple
                        options={eventsWithPackingLists}
                        getOptionLabel={option => String(option.name ?? '')}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={eventsWithPackingLists.filter(e => selectedEventIds.has(e.id))}
                        onChange={(_e, newValue) => {
                            setSelectedEventIds(new Set(newValue.map(e => e.id)));
                            setValidationError(null);
                        }}
                        renderInput={params => <TextField {...params} placeholder="Select events" size="small" />}
                        renderOption={(props, event) => {
                            const { itemCount, trailerNames } = getPackingListStats(packingListItemsByEvent, event.id);
                            const dateStr = event.startDate
                                ? new Date(event.startDate as unknown as string).toLocaleDateString()
                                : 'No date';
                            return (
                                <li {...props} key={event.id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            py: 0.5,
                                        }}
                                    >
                                        <Typography variant="body1">{event.name ?? ''}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {dateStr}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {itemCount} item{itemCount !== 1 ? 's' : ''}
                                            </Typography>
                                            {trailerNames.length > 0 && (
                                                <>
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        •
                                                    </Typography>
                                                    {trailerNames.map(n => (
                                                        <Chip key={n} label={n} size="small" variant="outlined" />
                                                    ))}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </li>
                            );
                        }}
                    />
                )}

                {validationError && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {validationError}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving || tripLocked}>
                    {isSaving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
